import type { Draft, OutboundLogRow } from "./types";

// What the Mail screen and a sent vendor PO show from the outbound guard's log (outbound_log, each row tied to its
// email by draftId) and the drafts (outbound_drafts). Pure, so it is tested without a browser.

export type Tone = "late" | "soon" | "good" | "neutral";

/**
 * A log row as the screens read it: the guard records whatever it was asked to send, so old or refused rows can lack a
 * purpose, a subject or lists (dev's guard tests do). Missing parts become empty, so one odd row never stops a screen.
 */
export function logRow(raw: Partial<OutboundLogRow>): OutboundLogRow {
  return {
    outboundId: raw.outboundId ?? 0, at: raw.at ?? "", decision: raw.decision ?? "", reason: raw.reason ?? null,
    mode: raw.mode ?? "SIMULATION", purpose: raw.purpose ?? "", source: raw.source ?? "", from: raw.from ?? "",
    subject: raw.subject || "(no subject)", intendedTo: raw.intendedTo ?? [], intendedCc: raw.intendedCc ?? [],
    deliveredTo: raw.deliveredTo ?? [], draftId: raw.draftId ?? null, vendorPo: raw.vendorPo ?? null, attachments: raw.attachments ?? [],
  };
}

/** The guard's decision in words, never colour alone. */
export function decisionWords(decision: string | null | undefined): { text: string; tone: Tone } {
  switch (decision) {
    case "SENT": return { text: "sent", tone: "good" };
    case "REDIRECTED": return { text: "sent to simulation recipients", tone: "good" };
    case "DRAFTED": return { text: "drafted for review", tone: "soon" };
    case "BLOCKED": return { text: "blocked", tone: "late" };
    case "FAILED": return { text: "failed", tone: "late" };
    case "DRY_RUN": return { text: "dry run", tone: "neutral" };
    default: return { text: (decision ?? "unknown").toLowerCase().replace(/_/g, " "), tone: "neutral" };
  }
}

/** A vendor PO's email that went: the SENT draft for it, the latest if there were several. */
export function sentEmailFor(vendorPoNumber: string, drafts: Draft[]): Draft | undefined {
  return drafts
    .filter((d) => d.vendorPo === vendorPoNumber && d.status === "SENT")
    .sort((a, b) => (b.reviewedAt ?? b.createdAt).localeCompare(a.reviewedAt ?? a.createdAt))[0];
}

/** Who sent it, from the log's source: "mcp:brian@tare.group" (a person, in the app or through Claude) or the platform. */
export function sentBy(source: string | null | undefined): string {
  if (!source) return "";
  const m = /^mcp:(.+)$/.exec(source);
  if (m) return m[1];
  if (source.startsWith("platform:")) return `the platform (${source.slice(9).replace(/-/g, " ")})`;
  return source;
}
